import { filetypeextension, filetypemime } from 'magic-bytes.js'

import { UploadedFileType } from '@/graphql/generated/schemas'

import {
  FILENAME_INCORRECT_CHARACTERS,
  FILENAME_TOO_LONG,
  FILE_CONTENT_MISMATCH,
  FILE_INCORRECT_EXTENSION,
  FILE_INCORRECT_EXTENSIONS,
  FILE_INCORRECT_TYPE,
  FILE_TOO_LARGE,
  UploadError,
} from './error'
import { FileTypePolicyI, getFileTypePolicy } from './fileTypePolicies'
import { ALLOWED_FILENAME_CHARS_REGEX } from './filenameValidation'
import { fileToArrayBuffer, getExtension, haveCommonElement } from './helpers'

export interface ValidationOptionsI {
  maxFileNameLength?: number
  maxFileSizeInBytes?: number
  messages?: {
    filenameIncorrectCharacters?: string
  }
}

const DEFAULT_MAX_FILENAME_LENGTH = 255

export const validateFile = async (
  file: File,
  fileType: UploadedFileType,
  validationOptions?: ValidationOptionsI
): Promise<void> => {
  // File name validation
  const maxFileNameLength =
    validationOptions?.maxFileNameLength || DEFAULT_MAX_FILENAME_LENGTH
  validateFileName(file, maxFileNameLength, validationOptions?.messages)

  // File size validation (optional)
  if (validationOptions?.maxFileSizeInBytes) {
    validateFileSize(file, validationOptions?.maxFileSizeInBytes)
  }

  // File type validation: extension and MIME type
  const policy = getFileTypePolicy(fileType)
  if (policy) {
    await validateFileType(file, policy)
  }
}

const validateFileType = async (file: File, policy: FileTypePolicyI) => {
  const {
    extensions: allowedExtensions,
    mimes: allowedMimes,
    unsignedExtensions,
  } = policy

  const filenameExtension = getExtension(file.name).toLowerCase()
  // Formats without a reliable signature are matched on filename + MIME only
  const isUnsignedExtension =
    unsignedExtensions?.includes(filenameExtension) ?? false
  const shouldUseMagicNumber =
    policy.shouldUseMagicNumber && !isUnsignedExtension

  let fileExtensions: string[]
  let fileMimes: string[]

  if (shouldUseMagicNumber) {
    // Determine file type based on "magic number"
    // Reference: <https://en.wikipedia.org/wiki/List_of_file_signatures>
    // This doesn't work for text-based files like CSV
    const arrayBuffer = await fileToArrayBuffer(file)
    const uint = new Uint8Array(arrayBuffer)

    fileExtensions = filetypeextension(uint)
    fileMimes = filetypemime(uint)
  } else {
    // Simpler form based on File API only
    // Normalize to lowercase so uploads like FILE.CSV or photo.JPG are accepted
    fileExtensions = [filenameExtension]
    fileMimes = [file.type]
  }

  // Validate file extension
  if (
    allowedExtensions !== '*' &&
    !haveCommonElement(allowedExtensions, fileExtensions)
  ) {
    const context = {
      fileName: file.name,
      fileType: file.type,
      allowedExtensions,
    }
    // The filename is fine but the bytes disagree with it — reporting the
    // magic-number guess here would name a format the user never picked
    if (shouldUseMagicNumber && allowedExtensions.includes(filenameExtension)) {
      throw new UploadError(FILE_CONTENT_MISMATCH(filenameExtension), context)
    }
    const uploadedExtension = filenameExtension || undefined
    if (allowedExtensions.length === 1) {
      throw new UploadError(
        FILE_INCORRECT_EXTENSION(allowedExtensions[0], uploadedExtension),
        context
      )
    }
    throw new UploadError(
      FILE_INCORRECT_EXTENSIONS(allowedExtensions, uploadedExtension),
      context
    )
  }

  // Some browsers report an empty MIME type for .csv and .doc. The extension
  // check above already passed, so there is nothing left to match against.
  if (isUnsignedExtension && !file.type) {
    return
  }

  // Validate file MIME type
  if (allowedMimes !== '*' && !haveCommonElement(allowedMimes, fileMimes)) {
    throw new UploadError(FILE_INCORRECT_TYPE, {
      fileName: file.name,
      fileType: file.type,
    })
  }
}

const validateFileName = (
  file: File,
  maxLength: number,
  messages?: ValidationOptionsI['messages']
) => {
  const fileName = file.name

  if (!ALLOWED_FILENAME_CHARS_REGEX.test(fileName)) {
    throw new UploadError(
      messages?.filenameIncorrectCharacters ?? FILENAME_INCORRECT_CHARACTERS,
      { fileName }
    )
  }
  if (fileName.length > maxLength) {
    throw new UploadError(FILENAME_TOO_LONG(maxLength), {
      fileName,
      maxFileNameLength: maxLength,
    })
  }
}

const validateFileSize = (file: File, maxSizeInBytes: number) => {
  const fileSize = file.size

  if (fileSize > maxSizeInBytes) {
    throw new UploadError(FILE_TOO_LARGE, {
      fileName: file.name,
      fileSize,
      maxFileSizeInBytes: maxSizeInBytes,
    })
  }
}
